import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import DocumentTitleFormatter from '../../utilities/documentTitleFormatter';
import MapRouteTitle from '../../utilities/mapRouteTitle';
import { withRouteOnEnter } from '../../utilities/withRouteOnEnter.component';
import Auth from '../auth/auth';
import { clearAuth, setAuth } from '../auth/auth.actions';
import { isAuthenticated } from '../auth/authOperations';
import Welcome from '../auth/welcome.component';
import Callback from '../callback/callback';
import SurveyLanding from '../surveys/landing/surveyLanding.container';
import Toast from './toast.container';
import LoadingOverlay from '../layout/loadingOverlay.component';
import NotFound from '../layout/404.container';
import Imprint from '../layout/imprint.container';
import Terms from '../layout/terms.container';
import Privacy from '../layout/privacy.container';
import Footer from './footer.component';
import { EventBus } from '@survey/common/dist/utilities/EventBus';
import Confirm from '@survey/common/dist/components/dialogs/Confirm';
const styles = (theme) => ({
  root: {
    height: '100%',
  },
  mainContent: {
    height: '100%',
  },
});

class MainContainer extends Component {
  constructor(props) {
    super(props);

    //this.auth = new Auth(this.props.location.search === '?internal' ? true : false, this.props.history, props.setAuth, props.clearAuth);
    this.auth = new Auth(this.props.history, props.setAuth, props.clearAuth);

    this.checkAuth = this.checkAuth.bind(this);
    this.handleResendVerificationEmail = this.handleResendVerificationEmail.bind(this);
    this.isEmailUnverified = this.isEmailUnverified.bind(this);
    this.refreshAuthClaims = this.refreshAuthClaims.bind(this);
    this.handleReauthenticate = this.handleReauthenticate.bind(this);

    this.state = {
      resendVerificationPending: false,
      resendVerificationMessage: '',
      resendVerificationFailed: false,
    };

    //set a variable to control if we are using auth0 logins or not.
    this.useAuth = false;

    let rawIdToken = localStorage.getItem('id_token');
    props.setAuth(rawIdToken);
    
    if (process.env.REACT_APP_AUTH0_USE_AUTH === 'true') {
      this.useAuth = true;
    }
  }

  componentDidMount() {
    if (this.props.location.pathname !== '/login' && this.props.location.pathname !== '/callback') {
      localStorage.setItem('redirect_uri', this.props.location.pathname);
    }
    if (process.env.REACT_APP_ENVIRONMENT === 'germantender') {
      document.body.style.setProperty('font-family', "'Century Gothic', CenturyGothic, AppleGothic, sans-serif", 'important');
    }

    EventBus.on('LogOff', () => {
      this.logOut();
    });

    this.refreshAuthClaims();
  }

  componentWillUnmount() {
    EventBus.remove('LogOff');
  }

  logOut() {
    this.auth.logout();
  }

  checkAuth() {
    if (!this.useAuth) {
      return;
    }

    if (isAuthenticated(this.props.tokenInfo)) {
      if (this.isEmailUnverified(this.props.tokenInfo)) {
        if (this.props.location.pathname !== '/home') {
          this.props.history.replace('/home');
        }
        return;
      }

      if (this.props.location.pathname === '/login') {
        this.props.history.replace('/home');
      }
    } else {    
        this.auth.login();
    }
  }

  isEmailUnverified(tokenInfo) {
    if (!tokenInfo) {
      return false;
    }

    return tokenInfo.email_verified === false || tokenInfo.emailVerified === false;
  }

  async refreshAuthClaims() {
    if (!this.useAuth || !isAuthenticated(this.props.tokenInfo)) {
      return;
    }

    try {
      await this.auth.renewSession();
    } catch (error) {
      // Silent failure - no action needed
    }
  }

  handleReauthenticate() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('expires_at');
    localStorage.setItem('redirect_uri', '/home');
    this.props.clearAuth();
    this.auth.forceReauthenticate();
  }

  async handleResendVerificationEmail() {
    if (this.state.resendVerificationPending) {
      return;
    }

    this.setState({
      resendVerificationPending: true,
      resendVerificationMessage: '',
      resendVerificationFailed: false,
    });

    try {
      const accessToken = localStorage.getItem('access_token');

      if (!accessToken) {
        throw new Error('Missing access token');
      }

      let apiBaseUrl = process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL : 'https://surveybuilder.dev.himssanalytics.org/api/';

      const response = await fetch(`${apiBaseUrl}ModelAssessment/ResendAuthenticatedVerificationEmail`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('ResendAuthenticatedVerificationEmail failed');
      }

      this.setState({
        resendVerificationPending: false,
        resendVerificationMessage: 'Verification email sent. Please check your inbox.',
        resendVerificationFailed: false,
      });
    } catch (error) {
      this.setState({
        resendVerificationPending: false,
        resendVerificationMessage: 'Unable to resend verification email. Please try again.',
        resendVerificationFailed: true,
      });
    }
  }

  render() {
    const { classes, authSet, tokenInfo, location } = this.props;
    const { resendVerificationPending, resendVerificationMessage, resendVerificationFailed } = this.state;
    const isUnverifiedAuthenticatedUser = this.useAuth && isAuthenticated(tokenInfo) && this.isEmailUnverified(tokenInfo);
    const verificationDialogBaseMessage =
      'We found your account, but your email address is not verified yet. Please verify your email by clicking the Auth0 verification link in your email.';
    const verificationDialogStatusMessage = resendVerificationMessage
      ? `<br/><br/><span style="color: ${resendVerificationFailed ? 'red' : 'green'}"><strong>${resendVerificationMessage}</strong></span>`
      : '';

    //Setting a White Label product title based on enviornments.
    var whiteLabelName = ({   
      "himss"        : "HIMSS Survey",
      "dod"          : "HIMSS Survey",
      "germantender" : "Digital Radar",
    })[process.env.REACT_APP_ENVIRONMENT];

    document.title = DocumentTitleFormatter(MapRouteTitle(location), whiteLabelName);
    
    return (
      <Fragment>
        {!authSet ? (
          <LoadingOverlay />
        ) : (
          <div className={classes.root}>
            <div className={classes.mainContent}>
              {this.useAuth ? (
                isAuthenticated(tokenInfo) ? (
                  isUnverifiedAuthenticatedUser ? (
                    <Switch>
                      <Route exact path="/home" render={() => <div />} />
                      <Route path="/imprint" component={Imprint}></Route>
                      <Route path="/terms" component={Terms}></Route>
                      <Route path="/privacy" component={Privacy}></Route>
                      <Redirect from="*" to="/home" />
                    </Switch>
                  ) : (
                    <Switch>
                      <Route exact path="/home" component={withRouteOnEnter(this.checkAuth)(Welcome, this.props)} />
                      <Route path="/surveyLanding/:id" component={withRouteOnEnter(this.checkAuth)(SurveyLanding, this.props)} />
                      <Route path="/imprint" component={Imprint}></Route>
                      <Route path="/terms" component={Terms}></Route>
                      <Route path="/privacy" component={Privacy}></Route>
                      <Redirect from="*" to="/home" />
                    </Switch>
                  )
                ) : (
                  <Switch>
                    <Route path="/login" render= {(props)=>this.auth.login() } />
                    <Route
                      exact
                      path="/callback"
                      render={(props) => {
                        this.auth.handleAuthentication(props);
                        return <Callback {...props} />;
                      }}
                    />
                    <Route path="/imprint" component={Imprint}></Route>
                    <Route path="/terms" component={Terms}></Route>
                    <Route path="/privacy" component={Privacy}></Route>
                    
                    <Redirect from="*" to="/login" />
                  </Switch>
                )
              ) : (
                <Switch>
                  <Route path="/surveyLanding/:id" component={withRouteOnEnter(this.checkAuth)(SurveyLanding, this.props)} />
                  <Route path="/imprint" component={Imprint}></Route>
                  <Route path="/terms" component={Terms}></Route>
                  <Route path="/privacy" component={Privacy}></Route>
                  <Route component={NotFound} />
                </Switch>
              )}
            </div>
            <Confirm
              title="Email Verification Required"
              contentText={`${verificationDialogBaseMessage}${verificationDialogStatusMessage}`}
              open={isUnverifiedAuthenticatedUser}
              buttons="confirm-cancel"
              confirmText={resendVerificationPending ? 'Sending...' : 'Resend Verification Email'}
              cancelText="I've Verified My Email"
              disableConfirm={resendVerificationPending}
              onClose={(event, reason) => {
                if (reason === 'backdropClick') {
                  return;
                }

                this.handleReauthenticate();
              }}
              onConfirm={this.handleResendVerificationEmail}
            />
            <Toast />
            <Footer />
          </div>
        )}
      </Fragment>
    );
  }
}

MainContainer.propTypes = {
  // Injected by React Router
  children: PropTypes.node,
};

const mapStateToProps = (state) => {
  return {
    authSet: state.auth.get('authSet'),
    tokenInfo: state.auth.get('tokenInfo'),
    roles: state.auth.get('roles'),
    permissions: state.auth.get('permissions'),
  };
};

export default withStyles(styles)(
  withRouter(
    connect(mapStateToProps, {
      setAuth,
      clearAuth,
    })(MainContainer)
  )
);
